// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** ChatCapability — the build-once, shared mission-room chat capability.
 *
 *  It IS a capability (CONTRACT:C3-CAPABILITY-PLUGIN.1.0): it implements
 *  `Capability` and touches ONLY the injected `HostContext` (light coupling). The
 *  same module runs verbatim in the tak-tdf COP and in blindpipe — each supplies
 *  its own HostContext (engine/bus/tokens/policy) and its own I5 E2E port.
 *
 *  What it contributes:
 *   - one dockable "chat" panel (registerPanel),
 *   - command-palette verbs: open a room, send a message, switch room (registerCommand),
 *   - it subscribes to the envelope bus for `kind: "message"` chat envelopes,
 *   - it publishes chat-message envelopes (body sealed via the injected I5 port).
 *
 *  Data model: each message is a CanonicalEnvelope; rooms map to missions; presence
 *  + per-room ordering are maintained in-memory. E2E: the body is sealed into
 *  payload.encrypted_ref by the injected ChatKeyAccessPort (mock in tests, the real
 *  COP chatKeyAccess in production), so an unauthorized reader gets the SHELL not the
 *  body.
 *
 *  ROOM MODEL (§3, L2 — CEILING semantics, flipped 2026-07-06): a room's creator-set
 *  classification + releasability is the ROOM MARKING. The room class is the SEAL
 *  (transport) classification for ALL room traffic — every message envelope is published
 *  at EXACTLY the room class — AND the CEILING for the operator's per-message CONTENT
 *  pick: you may type at-or-below the room class (the pick rides INSIDE the sealed body
 *  as `contentClassification`), NEVER above (ROOM_CEILING — open a higher room for that
 *  traffic). Control envelopes (presence/descriptor) still clamp UP to the room marking
 *  — that clamp IS the Glomar guard (an envelope below the room class would deliver the
 *  room's name to uncleared subjects) — and the audience (releasability) NEVER widens.
 *  A per-room DESCRIPTOR envelope (§4.1) makes a zero-message room listable and the
 *  marking authoritative before the first message.
 *
 *  GLOMAR ROOM LIST (§4, C4): `rooms()` is a PURE FOLD of the already-PDP-cleared
 *  ingested message + descriptor envelopes grouped by `security.mission[0]`. There is
 *  NO pre-filter room registry ahead of the PDP — a denied room never reaches ingest,
 *  so it is ABSENT from the fold (no row, no badge, no presence). All room-derived
 *  counts fold off the SAME post-seam store.
 *
 *  IDENTITY-SWITCH REBUILD (§4.3, R2): `setSubject` CLEARS the per-room decoded-body
 *  store and re-ingests ONLY the currently-visible (post-PDP-seam) envelopes for the
 *  new subject. A HIGH body decoded under HIGH VANISHES on a switch-to-LOW (the store
 *  is rebuilt from scratch — default to HIDING, never retain the prior persona's rooms
 *  or bodies). (The §2.2 plaintext cache is cleared in lockstep by the host adapter.)
 *
 *  Crash isolation is the HOST's job (CapabilityHost wraps every callback). This
 *  module therefore never tries to swallow host errors; it just behaves. */

import type { CanonicalEnvelope } from "../../schema/ts/envelope.ts";
import type {
  Capability,
  HostContext,
  Disposable,
  PolicyRequest,
} from "../../contracts-ts/src/index.ts";
import type { ChatKeyAccessPort } from "./keyaccess.ts";
import {
  type ChatMessage,
  type RosterContact,
  type MessageSigner,
  buildMessageEnvelope,
  buildClearMessageEnvelope,
  buildRoomDescriptorEnvelope,
  contactFromPresence,
  decodeMessage,
  isChatMessage,
  isRoomDescriptor,
  envelopeRoom,
  orderMessages,
  roomLabels,
  classRank,
  classShort,
  roomLegacyPresence,
  legacyCallsignsInRoom,
  PRESENCE_AUTHOR,
} from "./messages.ts";

/** The fixed broadcast room. UNCLASSIFIED floor, ALWAYS present, NEVER Glomar-hidden
 *  (TAK-CHAT-SEMANTICS §3.1 / §4 risk). */
export const ALL_CHAT_ROOMS = "All Chat Rooms";

/** Releasability tokens that DO NOT narrow the audience (a broadcast/everyone marking). Any
 *  OTHER non-empty token is an audience RESTRICTION (it binds an ABAC FQN that gates readers),
 *  so it arms the room-marking chokepoint's fail-closed-on-unknown-floor guard. Mirrors
 *  kas.ts:PERMISSIVE_REL so the cap-chat clamp and the seal-side guard agree. */
const PUBLIC_REL = new Set(["ALL", "PUBLIC", "UNRESTRICTED", "EVERYONE", "*"]);

/** How long a presence beacon stays FRESH (the SA stale window). A capability verdict reads
 *  a contact as live until `now > stale`; the COP re-beacons within this window. C5, §5.1. */
const PRESENCE_STALE_MS = 75_000;

/** Construction options for the chat capability. */
export interface ChatCapabilityOptions {
  /** The injected E2E port (CONTRACT:I5-KEY-ACCESS.1.0 projection). REQUIRED:
   *  the body is never sent in the clear. */
  keyAccess: ChatKeyAccessPort;
  /** The acting subject's ABAC attributes — used for rewrap on incoming messages
   *  and as author identity. Must carry an `id`. */
  subject: { id: string } & Record<string, unknown>;
  /** Stable capability id (default "scop:cap:chat"). */
  id?: string;
  /** Initial room to open on activate (default ALL_CHAT_ROOMS). */
  initialRoom?: string;
  /** Is THIS client TDF-capable (C5, §5.1)? When true (the default — the COP is a sealed
   *  client), every presence beacon this client emits advertises `<__tdfcap v="1"
   *  alg="ZTDF"/>` so peer COPs detect it as `capable`. A test/legacy harness can set this
   *  false to model a non-advertising (legacy) emitter. */
  tdfCapable?: boolean;
  /** LIVE getter for the operator's DISPLAY CALLSIGN (the COP settings `user.callsign`
   *  leaf — a getter because the leaf can change without a re-login). When it returns a
   *  non-empty string, sends stamp `senderCallsign` on the clear shell and presence
   *  beacons carry `callsign`, so peers can render the callsign instead of the raw id.
   *  DISPLAY-ONLY + client-asserted — never an identity/authorization input. */
  callsign?: () => string | undefined;
  /** OPTIONAL DEMO-TIER MESSAGE SIGNER (getter, wired by the COP host at login to a WebCrypto
   *  session signer). When present, `send()` signs each message AFTER sealing, over the SEAL +
   *  id|room|sender (Guardrail 1). ADDITIVE — omit it and messages are simply unsigned
   *  ("asserted, not signed", the honest current state). Client-asserted trust; never a gate. */
  signer?: () => MessageSigner | undefined;
  /** OPTIONAL OUTBOUND CoT EGRESS (S1-TAK-GATEWAY): a fire-and-forget hook the host injects
   *  to cross a just-published chat envelope to the TAK net via the gateway POST /emit (the
   *  COP adapter wires the same-origin fetch in emitClient.ts, gated OFF by default). When
   *  present, send()/sendClear() invoke it AFTER the local ctx.data.publish — with the SAME
   *  sealed/bridged envelope — so the SEALED body rides the `<__tdf>` inner on the wire.
   *
   *  CONTRACT (cap-chat keeps no host coupling, so the hook MUST be the egress's whole story):
   *   - NON-BLOCKING + FAIL-SOFT — it MUST NOT throw or reject into the chat send (the local
   *     publish has already happened and ALWAYS stands). cap-chat invokes it fire-and-forget
   *     and never awaits it; the hook itself swallows + logs its own errors.
   *   - When ABSENT (the default — normal dev/mock, every test that doesn't assert egress),
   *     there is NO egress: behaviour is byte-identical to before this seam existed. */
  emit?: ChatEmitFn;
}

/** OUTBOUND CoT EGRESS hook (S1-TAK-GATEWAY). Hands one just-published CanonicalEnvelope to
 *  the host's egress client (the gateway POST /emit). FIRE-AND-FORGET: cap-chat never awaits
 *  it and the implementation MUST be fail-soft (swallow its own network/HTTP errors) so a
 *  gateway hiccup can never disturb the local chat send. */
export type ChatEmitFn = (env: CanonicalEnvelope) => void;

/** A read-only snapshot of one room (for the panel to render). */
export interface RoomView {
  room: string;
  /** Ordered messages (oldest → newest), each shell-or-body per the subject's grant. */
  messages: ChatMessage[];
  /** Author ids currently present in the room. */
  present: string[];
}

/** A Glomar-filtered room row (C4). Derived STRICTLY from post-PDP-seam ingested
 *  envelopes — a denied room never produces a row. */
export interface RoomRow {
  /** The room key (== security.mission[0]). */
  room: string;
  /** Display name (from a descriptor, else the room key). */
  displayName: string;
  /** The ROOM CLASSIFICATION (descriptor-authoritative; else the highest-marked visible
   *  envelope). Under ceiling semantics this is the seal class of all room traffic AND the
   *  ceiling for the composer's content pick. */
  classification: string;
  /** The room's RELEASABILITY (the descriptor's audience marking, L2) — so the composer
   *  can re-pin its RelPicker to it on room switch (H1: a wider pick is clamped, but the
   *  UI defaulting correct avoids a confusing "(clamped)" surprise). Field name keeps the
   *  historical "floor" spelling (the audience is a bound envelopes clamp INTO, never
   *  widen). `undefined` when no descriptor is known (e.g. broadcast / a message-only room). */
  floorReleasability?: string[];
  /** Author ids present (folded off the post-seam store). */
  present: string[];
  /** Message count in this room (post-seam — never counts hidden traffic). */
  messageCount: number;
  /** ISO time of the newest message (or undefined for a zero-message room). */
  lastMessageTime?: string;
  /** Preview of the newest message body if cleared, else undefined (no leak). */
  lastMessagePreview?: string;
  /** True for the fixed broadcast room (never hidden). */
  fixed: boolean;
  /** UNREAD COUNT (CONTRACT:C3-CHAT.1.0 read-state): messages ordered after this client's
   *  per-room "last read" high-water that the SUBJECT did not author. Folds off the SAME
   *  post-seam store as every other count — never a side-channel for a hidden room. A
   *  soft-left (hidden) room contributes 0. */
  unread: number;
  /** MANDATORY (CONTRACT:C3-CHAT.1.0): true when this room is NON-removable — the fixed
   *  broadcast room OR a classification-floor room (descriptor class above UNCLASSIFIED).
   *  The switcher hides the leave affordance for a mandatory room; `leaveRoom` refuses it. */
  mandatory: boolean;
}

export class ChatCapability implements Capability {
  readonly id: string;

  private readonly keyAccess: ChatKeyAccessPort;
  /** The acting subject — REBUILT on identity switch (R2: no longer set-once). */
  private subject: { id: string } & Record<string, unknown>;
  private readonly initialRoom: string;
  /** Does THIS client advertise `<__tdfcap>` on its presence (C5, §5.1)? */
  private readonly tdfCapable: boolean;
  /** OPTIONAL outbound CoT egress hook (S1-TAK-GATEWAY) — fire-and-forget + fail-soft. When
   *  undefined (the default), there is NO egress and the local publish is the whole story. */
  private readonly emit?: ChatEmitFn;

  /** Per-room message store, keyed by room → (envelope id → ChatMessage). De-dup
   *  by id keeps re-delivered envelopes from duplicating. The DECODED body lives here;
   *  CLEARED + rebuilt on identity switch (R2). */
  private roomStore = new Map<string, Map<string, ChatMessage>>();
  /** Per-room presence set. Folded off the post-seam store — CLEARED on switch. */
  private presence = new Map<string, Set<string>>();
  /** ROSTER (C5, §5.1): the LIVE presence beacon per (room, uid), keyed `${room} ${uid}`.
   *  Each carries the parsed `<__tdfcap>` flag + the beacon's stale window, so the pure
   *  capability selectors (`capabilityOf` / `roomLegacyPresence`) re-derive a FRESH verdict
   *  every call off this snapshot — never a cached/sticky capability bit. CLEARED on identity
   *  switch (default-to-HIDING: the lower persona must re-learn the roster from cleared
   *  presence only). A LATER beacon for the same (room, uid) REPLACES the earlier one (the
   *  flag rides every beacon, so the newest stale window + flag is authoritative). */
  private roster = new Map<string, RosterContact>();
  /** Per-room descriptor (from a descriptor envelope) — CLEARED on switch. Drives the
   *  displayName + authoritative marking (classification + releasability) of a (possibly
   *  zero-message) room. The releasability is captured too so a room CONTROL envelope
   *  (presence/typing/receipt) can be sealed at the SAME floor a message/descriptor is —
   *  otherwise a control envelope leaks the room's existence past the PDP seam. */
  private descriptors = new Map<
    string,
    { displayName: string; classification: string; releasability: string[] }
  >();
  /** The RAW post-seam envelopes this capability has ingested (message + descriptor),
   *  keyed by id. The diff universe for the identity-switch rebuild. CLEARED on switch. */
  private rawEnvelopes = new Map<string, CanonicalEnvelope>();
  /** Monotonic per-room send sequence this client stamps. CLEARED on identity switch
   *  (R2-low — default-to-hiding completeness: no per-room counter survives cross-persona). */
  private seqCounters = new Map<string, number>();
  /** Rooms this subject JOINED whose presence was SUPPRESSED because the floor was not yet
   *  known (fail-closed, G1). When a descriptor for such a room arrives, presence is
   *  re-published at the now-known floor. CLEARED on identity switch. */
  private pendingPresence = new Set<string>();
  /** SOFT-LEAVE (CONTRACT:C3-CHAT.1.0): rooms this client HID from its OWN switcher. A
   *  LOCAL-ONLY view filter — it only ever REMOVES a room from the (already PDP-cleared)
   *  fold, so it can NEVER reveal a denied room (Glomar-safe by construction). The fixed
   *  broadcast room + any mandatory classification-floor room are NON-leavable. Rejoin via
   *  openRoom (which un-hides). CLEARED on identity switch (the new persona sees its full
   *  cleared list; un-hiding can only ever SHOW a room the subject is already cleared for). */
  private hiddenRooms = new Set<string>();
  /** READ-STATE (CONTRACT:C3-CHAT.1.0): per-room "last read" high-water order key. unread =
   *  messages ordered strictly AFTER it that the subject did not author. UX-only (never a
   *  gate). CLEARED on identity switch (read-state is per-persona; default-to-fresh). */
  private roomRead = new Map<string, string>();
  /** While rebuilding (identity switch), per-envelope notifyChange is SUPPRESSED so a
   *  mid-loop render can never read a stale/hidden currentRoom; one notifyChange fires at
   *  the end (R1 — no transient render leak, and N→1 renders). */
  private rebuilding = false;
  /** Change listeners (the panel adapter subscribes; fired after every store mutation
   *  — ingest / descriptor / presence / identity-switch rebuild). */
  private readonly changeListeners = new Set<() => void>();

  private ctx?: HostContext;
  private readonly disposables: Disposable[] = [];
  private currentRoom: string;

  /** Live operator-callsign getter (display-only; see ChatCapabilityOptions.callsign). */
  private readonly callsignOf?: () => string | undefined;
  /** Live demo-tier message-signer getter (see ChatCapabilityOptions.signer). */
  private readonly signerOf?: () => MessageSigner | undefined;

  constructor(opts: ChatCapabilityOptions) {
    this.id = opts.id ?? "scop:cap:chat";
    this.keyAccess = opts.keyAccess;
    this.subject = opts.subject;
    this.initialRoom = opts.initialRoom ?? ALL_CHAT_ROOMS;
    this.currentRoom = this.initialRoom;
    // Default: a COP IS a sealed/TDF-capable client → advertise `<__tdfcap>` on presence.
    this.tdfCapable = opts.tdfCapable ?? true;
    this.emit = opts.emit;
    this.callsignOf = opts.callsign;
    this.signerOf = opts.signer;
  }

  /** The operator's current display callsign, or undefined (empty/no getter). */
  private currentCallsign(): string | undefined {
    const cs = this.callsignOf?.();
    return typeof cs === "string" && cs.trim() ? cs.trim() : undefined;
  }

  /** Cross a just-published chat envelope to the TAK net (S1-TAK-GATEWAY). NO-OP when no
   *  egress hook is injected (the default). FIRE-AND-FORGET + FAIL-SOFT: a throwing hook is
   *  caught here so egress can NEVER disturb a chat send — the local publish already stands.
   *  The hook receives the SAME envelope that went on the bus (sealed encrypted_ref intact),
   *  so the gateway re-wraps the sealed body into the `<__tdf>` inner. */
  private tryEmit(env: CanonicalEnvelope): void {
    if (!this.emit) return;
    try {
      this.emit(env);
    } catch (e) {
      // Belt-and-suspenders: the hook is contractually fail-soft, but a buggy host hook must
      // STILL never break the chat send. Swallow + log concisely (the publish already happened).
      // eslint-disable-next-line no-console
      console.error("[cap-chat] outbound emit hook threw (swallowed — local publish stands):", e);
    }
  }

  /** Permissions this capability DECLARES — the host policy-gates these before
   *  activation (CONTRACT:I4-POLICY-DECISION.1.0 via the host). Subscribing to the
   *  message bus is the gated action. */
  declares(room = this.initialRoom): PolicyRequest[] {
    return [
      { subject: this.subject, action: "subscribe", resource: roomLabels(room) },
    ];
  }

  /** The host calls this once with the HostContext (the ONLY surface we touch). A
   *  throw here is caught by the host as ErrCapabilityActivation (registers none). */
  activate(ctx: HostContext): void {
    this.ctx = ctx;

    // Out-of-map extension: the dockable chat panel.
    this.disposables.push(
      ctx.registerPanel({
        id: `${this.id}:panel`,
        title: "Chat",
        location: "right",
      }),
    );

    // Command-palette verbs.
    this.disposables.push(
      ctx.registerCommand({
        id: `${this.id}:send`,
        title: "Chat: Send message",
        category: "Chat",
        run: (args) => this.sendCommand(args),
      }),
    );
    this.disposables.push(
      ctx.registerCommand({
        id: `${this.id}:open-room`,
        title: "Chat: Open room",
        category: "Chat",
        run: (args) => this.openRoom(typeof args === "string" ? args : this.currentRoom),
      }),
    );

    // Read the envelope bus for chat messages (envelope-only data). The host's
    // ctx.data.subscribe is PDP-interposed, so a denied envelope NEVER reaches onEnvelope
    // (Glomar — the room store learns only cleared rooms, message by message).
    this.disposables.push(
      ctx.data.subscribe({ kinds: ["message"] }, (env) => {
        void this.onEnvelope(env);
      }),
    );

    // Join the initial room (presence + open).
    void this.openRoom(this.initialRoom);
  }

  /** Optional teardown; the host also force-tears-down on ErrCapabilityCrash. We
   *  dispose our own contributions defensively (the host disposes them too). */
  deactivate(): void {
    for (const d of this.disposables) {
      try {
        d.dispose();
      } catch {
        // best-effort; the host's registry is the source of truth
      }
    }
    this.disposables.length = 0;
    this.ctx = undefined;
  }

  // --- public read/write surface (used by the panel + commands) --------------

  /** Open / switch to a room. Two modes, distinguished by whether an EXPLICIT floor is
   *  given (G1 — the fail-closed presence guard must be REACHABLE):
   *   - CREATE: `classification` is provided (the creator/dropdown floor, L2). Publish a
   *     room DESCRIPTOR (so a cleared peer can list a zero-message room) AND presence,
   *     both at that explicit floor.
   *   - JOIN: `classification` is OMITTED (undefined — e.g. the command-palette open-room
   *     verb). We do NOT know the floor and are not the creator, so we publish NO
   *     descriptor; presence is published ONLY if the floor is already KNOWN from an
   *     ingested descriptor, otherwise it is SUPPRESSED (fail-closed) and re-published once
   *     the descriptor arrives. A join-by-name NEVER emits an UNCLASSIFIED presence
   *     carrying a restricted room's name. */
  async openRoom(room: string, classification?: string, releasability?: string[]): Promise<void> {
    this.currentRoom = room;
    // REJOIN un-hides a soft-left room (C3-CHAT) — opening a room is the inverse of leaving it.
    this.hiddenRooms.delete(room);
    this.markPresent(room, this.subject.id);
    // OPEN == READ (§7.7 mark-read-on-open): the room you switch into clears its unread badge.
    this.setReadHighWater(room);
    // CREATE: an explicit floor was chosen → publish the descriptor at that floor. JOIN
    // (no explicit floor) publishes no descriptor — the joiner isn't the authority.
    if (classification !== undefined) {
      // No explicit releasability on create → publish the descriptor at the PUBLIC,
      // UNRESTRICTED floor `[]` (release-to-everyone within the classification). A coalition
      // tag like NATO is NOT a legal defense.local releasability and threw ILLEGAL_RELEASABILITY
      // on every descriptor/presence seal; `[]` validates + seals.
      await this.publishRoomDescriptor(room, classification, releasability ?? []);
    }
    // Presence is sealed at the ROOM FLOOR (P1/G1). The floor is the EXPLICIT open-time
    // classification (create) OR an already-ingested descriptor; if NEITHER is known,
    // publishPresence fails closed (suppresses) and remembers the room for re-publish.
    await this.publishPresence(room, true, classification, releasability);
  }

  /** Send a message to a room (defaults to the current room). Enforces the room CEILING
   *  (L2 flip): the operator's classification pick must be <= the room class (throws
   *  ROOM_CEILING above it). The ENVELOPE is sealed at EXACTLY the room class (the Glomar
   *  pin — the shell names the room, so it must never travel below the room marking); the
   *  at-or-below pick becomes the CONTENT marking, carried INSIDE the sealed body (I5, R1
   *  — async seal awaited). Releasability is clamped to the room audience (never wider). */
  async send(
    body: string,
    room = this.currentRoom,
    classification = "UNCLASSIFIED",
    releasability?: string[],
  ): Promise<void> {
    if (!this.ctx) throw new Error("chat capability not active");
    // ROOM MARKING (L2, ceiling): pick <= room class (throw above); envelope class = the
    // room class via the chokepoint; releasability CLAMPED to the room audience (H1).
    const marking = this.assertRoomMarking(room, classification, releasability);
    const seq = this.nextSeq(room);
    const env = await buildMessageEnvelope(
      {
        id: `${this.id}:${room}:${this.subject.id}:${seq}:${Date.now()}`,
        room,
        author: this.subject.id,
        body,
        time: new Date().toISOString(),
        seq,
        // CONTENT MARKING: the operator's (ceiling-capped) pick rides INSIDE the sealed
        // body — never on the clear shell — so a granted reader's chip can show the actual
        // content level while the transport seal stays pinned at the room class.
        contentClassification: classification,
        // Display callsign (clear-shell, client-asserted) — peers render it over the raw id.
        senderCallsign: this.currentCallsign(),
      },
      this.keyAccess,
      marking.classification,
      marking.releasability,
      this.signerOf?.(),
    );
    await this.ctx.data.publish(env);
    // OUTBOUND EGRESS (S1-TAK-GATEWAY): cross the SEALED envelope to the TAK net via the
    // gateway POST /emit (no-op unless a host injected the hook). AFTER the local publish, so
    // the in-process chat is never gated on egress; fire-and-forget + fail-soft (tryEmit).
    this.tryEmit(env);
    // Reflect our own send locally (decode with our own subject → body visible).
    await this.ingest(env);
  }

  /** THE PLAINTEXT-BRIDGE CLEAR SEND (C5, §5.4) — the ONE armed envelope's clear build path.
   *  Drives `buildClearMessageEnvelope` (body in payload.clear.text, payload_mode:"clear")
   *  INSTEAD of the sealed `buildMessageEnvelope`. The COP state machine calls this for
   *  EXACTLY one envelope while armed; every other send goes through `send()` (sealed).
   *
   *  FAIL-CLOSED INDEPENDENT OF THE TOGGLE (§5.5): this STILL runs `assertRoomMarking`
   *  first — the room-CEILING + releasability gate applies to the clear envelope's labels
   *  EXACTLY as it does to a sealed send. The plaintext path can therefore NEVER ORIGINATE
   *  a body picked above the room class — it throws ROOM_CEILING (and the COP machine
   *  surfaces the error + keeps the draft), and no clear envelope is published. The clear
   *  BODY stays plain text (a legacy client must read it — no sealed-body carrier), so a
   *  bridged message carries no content marking; its labels bind at the room class. The
   *  toggle changes the BODY leakage, never the marking.
   *  Returns the published envelope's id (for the audit row). */
  async sendClear(
    body: string,
    room = this.currentRoom,
    classification = "UNCLASSIFIED",
    releasability?: string[],
  ): Promise<string> {
    if (!this.ctx) throw new Error("chat capability not active");
    // SAME room-marking gate as the sealed send (BOTH axes) — runs BEFORE we choose the clear
    // build path, so a pick above the room CEILING is refused AND the releasability is CLAMPED
    // to the room audience on the plaintext path too (fail-closed independent of the toggle,
    // H1/§5.5).
    const marking = this.assertRoomMarking(room, classification, releasability);
    const seq = this.nextSeq(room);
    const id = `${this.id}:${room}:${this.subject.id}:${seq}:${Date.now()}`;
    const env = buildClearMessageEnvelope(
      { id, room, author: this.subject.id, body, time: new Date().toISOString(), seq, senderCallsign: this.currentCallsign() },
      marking.classification,
      marking.releasability,
    );
    await this.ctx.data.publish(env);
    // OUTBOUND EGRESS (S1-TAK-GATEWAY): cross the BRIDGED (clear) envelope to the TAK net via
    // the gateway POST /emit (no-op unless a host injected the hook). The gateway's chat path
    // reads the §5 Plaintext-Bridge cleartext (payload.clear.text) onto the clear <remarks>
    // with no `<__tdf>` — exactly as the in-process bridge intends. Fire-and-forget + fail-soft.
    this.tryEmit(env);
    // Reflect our own send locally.
    await this.ingest(env);
    return id;
  }

  /** RE-DECODE the redacted (shell-only) messages for the CURRENT subject — the
   *  session-recovery hook. A body can be redacted for TWO reasons: a genuine clearance
   *  DENY, or an UNAUTHENTICATED failure (an expired access token — the KAS 401s and the
   *  row renders sealed even for the author). After the session is renewed (a silent
   *  refresh adopted a fresh token, or the operator re-authenticated), re-run
   *  decodeMessage over each redacted entry's RAW envelope and adopt any newly-GRANTED
   *  body. NEVER weakens fail-closed: a still-denied body stays redacted; the KAS remains
   *  authoritative on every re-decision; nothing is removed. Returns the recovered count. */
  async retryRedacted(): Promise<number> {
    let recovered = 0;
    for (const store of this.roomStore.values()) {
      for (const [id, msg] of store) {
        if (!msg.redacted || !msg.encryptedRef) continue;
        const raw = this.rawEnvelopes.get(id);
        if (!raw) continue;
        const again = await decodeMessage(raw, this.keyAccess, this.subject);
        if (!again.redacted) {
          store.set(id, again);
          recovered++;
        }
      }
    }
    if (recovered > 0) this.notifyChange();
    return recovered;
  }

  /** The known PEOPLE (id + display callsign when known) across every post-seam room —
   *  the DM-picker / autocomplete source. Glomar-safe by construction: folds ONLY off the
   *  post-seam presence + roster + message stores (a hidden room contributes no one). Minus
   *  self + the presence sentinel. Callsign is client-asserted display data.
   *
   *  CALLSIGN CHANNELS (both folded so a peer's callsign is known however it arrived):
   *   - a live PRESENCE beacon (`roster`) — the authoritative live display identity; and
   *   - the clear-shell `senderCallsign` a peer stamped on its MESSAGES (`roomStore`). Across
   *     the gateway this is the ONLY callsign channel that crosses today (presence beacons are
   *     in-process per COP), so a FEDERATED peer's callsign is learned from its traffic. This
   *     lets the DM autocomplete resolve a TYPED callsign back to the peer's CANONICAL id (so
   *     both windows key the DM on ids, not a display name — the room-divergence fix). A live
   *     beacon WINS over a message-derived callsign. */
  contacts(): { id: string; callsign?: string }[] {
    const byId = new Map<string, string | undefined>();
    for (const set of this.presence.values()) {
      for (const who of set) {
        if (who === this.subject.id || who === PRESENCE_AUTHOR) continue;
        if (!byId.has(who)) byId.set(who, undefined);
      }
    }
    // MESSAGE-DERIVED callsigns (lower precedence than a live beacon, filled below): a peer's
    // clear-shell senderCallsign rides its messages across the gateway even when its presence
    // does not. Client-asserted display data — never an identity/authorization input.
    for (const store of this.roomStore.values()) {
      for (const msg of store.values()) {
        if (msg.author === this.subject.id || msg.author === PRESENCE_AUTHOR) continue;
        if (msg.senderCallsign && !byId.get(msg.author)) byId.set(msg.author, msg.senderCallsign);
        else if (!byId.has(msg.author)) byId.set(msg.author, undefined);
      }
    }
    // Roster beacons carry the callsign (newest beacon per (room,uid) wins on ingest) — the
    // live presence identity WINS over any message-derived callsign.
    for (const c of this.roster.values()) {
      if (c.uid === this.subject.id || c.uid === PRESENCE_AUTHOR) continue;
      if (c.callsign) byId.set(c.uid, c.callsign);
      else if (!byId.has(c.uid)) byId.set(c.uid, undefined);
    }
    return [...byId.entries()]
      .map(([id, callsign]) => ({ id, ...(callsign ? { callsign } : {}) }))
      .sort((a, b) => (a.callsign ?? a.id).localeCompare(b.callsign ?? b.id));
  }

  // --- capability-detection selectors (C5, §5.2) — PURE folds off the LIVE roster --------

  /** The non-stale roster contacts (every room) — the snapshot the pure selectors fold over.
   *  Re-derived every call (never a cached capability bit). */
  rosterContacts(): RosterContact[] {
    return [...this.roster.values()];
  }

  /** Does the given (or current) room have ANY non-stale LEGACY participant (C5, §5.2)? This
   *  is the SINGLE gate that makes the Plaintext Bridge reachable. Folds off the LIVE roster;
   *  a stale contact is `unknown` (NOT legacy) so a gone client never makes it reachable. */
  roomLegacyPresence(room = this.currentRoom, now: Date = new Date()): boolean {
    return roomLegacyPresence(room, this.rosterContacts(), now);
  }

  /** The non-stale legacy callsigns in the given (or current) room — for the audit target set
   *  + the Wave-3b confirm modal. Folds off the SAME roster as the reachability gate. */
  legacyCallsigns(room = this.currentRoom, now: Date = new Date()): string[] {
    return legacyCallsignsInRoom(room, this.rosterContacts(), now);
  }

  /** The authoritative room-marking gate for a send (L2, CEILING semantics — flipped
   *  2026-07-06). Shared by the sealed AND the clear (bridge) paths so the gate is
   *  IDENTICAL on both — fail-closed independent of the toggle (§5.5).
   *
   *  CLASSIFICATION axis (the CEILING): the operator's pick MUST be <= the room class —
   *  throws ROOM_CEILING otherwise (traffic above the room's marking does not belong in
   *  this room; open a higher room instead). An at-or-below pick passes and becomes the
   *  message's CONTENT marking (carried inside the sealed body by the caller), while the
   *  ENVELOPE (transport) class returned here is EXACTLY the room class via the
   *  chokepoint's MAX — THE GLOMAR PIN: the clear shell names the room
   *  (security.mission), so an envelope sealed below the room class would be DELIVERED to
   *  subjects who must never learn the room exists. A below-room pick therefore lowers
   *  the CHIP, never the SEAL.
   *
   *  RELEASABILITY axis (H1 — UNCHANGED by the flip; security-load-bearing): the published
   *  message's releasability MUST be a SUBSET of the room's (narrower audience = more
   *  restrictive; a WIDER audience would surface an otherwise-Glomar-denied room + body to an
   *  out-of-coalition viewer, because each releasability token binds its own ABAC FQN). The
   *  effective releasability is the INTERSECTION of the operator's pick and the room's —
   *  mirroring ingestDescriptor's equal-class intersect ("never WIDEN the audience"). A pick
   *  that would widen beyond the room audience is CLAMPED to it (published at the room's
   *  releasability, never wider) — NOT thrown, so an over-broad RelPicker default can never
   *  leak. FAIL-CLOSED if the room releasability is UNKNOWN for a restricted/named room. */
  private assertRoomMarking(
    room: string,
    classification: string,
    releasability: string[] | undefined,
  ): { classification: string; releasability: string[] } {
    // CEILING: a pick ABOVE the room CLASS is an explicit operator error (throw) — distinct
    // from the releasability clamp (which silently narrows). Check it before the clamp.
    const roomClass = this.roomClass(room, classification);
    if (classRank(classification) > classRank(roomClass)) {
      throw new Error(
        `ROOM_CEILING: message classification "${classification}" is above room "${room}" class "${roomClass}" — open a higher room for this traffic (e.g. "${room}-${classShort(classification)}")`,
      );
    }
    // Route through the SINGLE chokepoint. A message is a RIDER (never establishes the room
    // marking). With a KNOWN descriptor the chokepoint's MAX(pick, roomClass) — given the
    // ceiling passed above (pick <= roomClass) — resolves to EXACTLY the room class: the
    // envelope seal is pinned at the room marking (the Glomar pin). An EXPLICIT restricted
    // releasability (the operator deliberately narrowed the audience) into a room with NO
    // known marking is treated as a BEACON-grade restriction → FAIL CLOSED (point 4 — we
    // can't prove the explicit audience is within the room's unknown audience). A
    // DEFAULT/omitted rel (undefined) carries no claimed restriction → the message rides on
    // its own class only (the existing UNCLAS message path), and the envelope defaults to the
    // public/unrestricted `[]` releasability downstream (release-to-everyone within the
    // classification).
    const relExplicitlyRestricted =
      releasability !== undefined && releasability.some((r) => !PUBLIC_REL.has(r.toUpperCase()));
    const clamped = this.clampMarkingToFloor(
      room,
      classification,
      releasability,
      /* establishFloor */ false,
      /* isBeacon */ relExplicitlyRestricted,
    );
    if (!clamped.ok) {
      // The ROOM_FLOOR error code is retained for this UNKNOWN-room-marking fail-closed path
      // (the releasability/audience axis — unchanged by the class-ceiling flip): the room's
      // marking floor for control/audience purposes is unknown, so we refuse to publish.
      throw new Error(`ROOM_FLOOR: ${clamped.reason} — refusing to publish (fail-closed; would risk widening the audience)`);
    }
    return { classification: clamped.classification, releasability: clamped.releasability };
  }

  /** Ordered, de-duplicated view of one room for the given (or acting) subject. G-nit:
   *  a room NOT in the room list (no store, no descriptor — i.e. a presence-only "ghost"
   *  room or a hidden room) returns present:[] so stray presence can never be queried for
   *  a room that does not appear in rooms() — presence folds off the same post-seam set. */
  view(room = this.currentRoom): RoomView {
    const store = this.roomStore.get(room);
    const messages = store ? orderMessages([...store.values()]) : [];
    const present = this.canSee(room) ? [...(this.presence.get(room) ?? new Set<string>())] : [];
    return { room, messages, present };
  }

  /** Rooms this client knows about (the POST-SEAM fold — a denied room is absent). */
  knownRooms(): string[] {
    return this.rooms().map((r) => r.room);
  }

  /** The acting subject's author id (sender identity + presence). */
  get author(): string {
    return this.subject.id;
  }

  /** The room this client is currently in. */
  get room(): string {
    return this.currentRoom;
  }

  /** Subscribe to store changes (fired after every ingest / descriptor / presence /
   *  identity-switch rebuild). Returns an unsubscribe. The panel adapter folds the
   *  post-seam store on each fire — never a side-channel for a hidden room (§4.4). */
  onChange(cb: () => void): () => void {
    this.changeListeners.add(cb);
    return () => {
      this.changeListeners.delete(cb);
    };
  }

  /** C5 — fire the change listeners after a PLAINTEXT-BRIDGE state transition (arm / revert).
   *  The bridge state lives in the COP adapter (not this class), but a transition must
   *  re-render the panel banner/countdown the SAME way a store mutation does, so the adapter
   *  drives this. A no-op during a rebuild (R1 — consistent with notifyChange). */
  notifyBridgeChange(): void {
    this.notifyChange();
  }

  /** GLOMAR ROOM LIST (C4, §4.2). A PURE FOLD of the already-cleared ingested
   *  message + descriptor envelopes grouped by `security.mission[0]`. A denied room
   *  never reached ingest, so it produces NO row. "All Chat Rooms" is a fixed
   *  UNCLASSIFIED floor row, always present. All counts fold off the post-seam store. */
  rooms(): RoomRow[] {
    const keys = new Set<string>([ALL_CHAT_ROOMS]);
    for (const room of this.roomStore.keys()) keys.add(room);
    for (const room of this.descriptors.keys()) keys.add(room);

    const out: RoomRow[] = [];
    for (const room of keys) {
      // SOFT-LEFT rooms are hidden from THIS client's switcher (C3-CHAT). The fixed broadcast
      // room is NEVER hideable. Hiding only ever REMOVES an already-cleared row, so it can
      // never reveal a Glomar-denied room (a denied room is already absent from `keys`).
      if (room !== ALL_CHAT_ROOMS && this.hiddenRooms.has(room)) continue;
      const store = this.roomStore.get(room);
      const ordered = store ? orderMessages([...store.values()]) : [];
      const last = ordered.length ? ordered[ordered.length - 1] : undefined;
      const desc = this.descriptors.get(room);
      out.push({
        room,
        displayName: desc?.displayName ?? room,
        // ROW-BADGE DERIVE (Wave-2 nit, DISPLAY ONLY — does NOT change any gating). The
        // descriptor's creator-set marking is authoritative when known (L2); a room with NO
        // descriptor derives its displayed classification from the MAX marking across its
        // own ingested (post-seam) message envelopes — so a message-only SECRET room shows
        // SECRET, not the bare UNCLASSIFIED fallback. `assertRoomMarking`/`roomClass` (the
        // GATING path) is UNCHANGED — this lives only in the row fold.
        classification: desc ? desc.classification : this.maxMessageClassification(room),
        // The room audience (descriptor-authoritative). Drives the composer RelPicker re-pin
        // on room switch (H1b) so the operator default is the room audience, not a fixed ['NATO'].
        floorReleasability: desc ? desc.releasability : undefined,
        present: [...(this.presence.get(room) ?? new Set<string>())],
        messageCount: ordered.length,
        lastMessageTime: last?.time,
        // Preview ONLY a cleared (non-redacted) body — never leak a sealed body.
        lastMessagePreview: last && !last.redacted ? last.body : undefined,
        fixed: room === ALL_CHAT_ROOMS,
        // READ-STATE + REMOVABILITY (C3-CHAT). Both fold off the SAME post-seam store as every
        // other field — a hidden room is already excluded above, a denied room never reached
        // ingest. unread counts post-seam messages only; mandatory gates the leave affordance.
        unread: this.unreadCount(room),
        mandatory: this.isMandatoryRoom(room),
      });
    }
    // Newest-traffic first; the fixed broadcast room sorts to the top.
    out.sort((a, b) => {
      if (a.fixed !== b.fixed) return a.fixed ? -1 : 1;
      const at = a.lastMessageTime ?? "";
      const bt = b.lastMessageTime ?? "";
      return at < bt ? 1 : at > bt ? -1 : a.room < b.room ? -1 : 1;
    });
    return out;
  }

  // --- READ-STATE + SOFT-LEAVE (CONTRACT:C3-CHAT.1.0) -------------------------------------

  /** The sortable "order key" for a message — mirrors orderMessages' comparison (time, then
   *  per-room seq, then id) as ONE lexicographically-comparable string. Used as the read
   *  high-water so unread = messages whose key is strictly greater than the mark. */
  private orderKey(m: ChatMessage): string {
    const seq = String(Math.max(0, m.seq | 0)).padStart(20, "0");
    return `${m.time} ${seq} ${m.id}`;
  }

  /** Advance a room's read high-water to its newest message (no notify — callers batch). A
   *  zero-message room keeps its prior mark (default ""). */
  private setReadHighWater(room: string): void {
    const store = this.roomStore.get(room);
    let max = this.roomRead.get(room) ?? "";
    if (store) {
      for (const m of store.values()) {
        const k = this.orderKey(m);
        if (k > max) max = k;
      }
    }
    this.roomRead.set(room, max);
  }

  /** UNREAD COUNT for a room (default current): post-seam messages ordered strictly after the
   *  read high-water that the SUBJECT did not author. A hidden (soft-left) room reads 0. Pure
   *  fold off the post-seam store — never counts hidden/denied traffic (§4.4 / §7.7). */
  unreadCount(room = this.currentRoom): number {
    if (this.hiddenRooms.has(room)) return 0;
    const store = this.roomStore.get(room);
    if (!store) return 0;
    const mark = this.roomRead.get(room) ?? "";
    let n = 0;
    for (const m of store.values()) {
      if (m.author === this.subject.id) continue; // your own messages are read by definition
      if (this.orderKey(m) > mark) n++;
    }
    return n;
  }

  /** TOTAL UNREAD across every VISIBLE (non-hidden) room — the global badge (§7.7). */
  totalUnread(): number {
    let n = 0;
    for (const room of this.roomStore.keys()) {
      if (this.hiddenRooms.has(room)) continue;
      n += this.unreadCount(room);
    }
    return n;
  }

  /** Mark a room (default current) READ — advances its high-water to the newest message so its
   *  unread badge clears (§7.7 mark-read-on-open). Notifies once. */
  markRoomRead(room = this.currentRoom): void {
    this.setReadHighWater(room);
    this.notifyChange();
  }

  /** Mark EVERY visible room READ (bulk "mark all read"). Notifies once. */
  markAllRoomsRead(): void {
    for (const room of this.roomStore.keys()) {
      if (!this.hiddenRooms.has(room)) this.setReadHighWater(room);
    }
    this.notifyChange();
  }

  /** Is `room` MANDATORY (non-removable)? The fixed broadcast room is always mandatory; a
   *  classification-floor room (a known descriptor whose class is above UNCLASSIFIED) is
   *  mandatory — you cannot opt out of a compartment you are cleared into. Optional ad-hoc /
   *  DM / UNCLASSIFIED rooms are leavable (ASYNC-BUNDLES Decisions log). */
  isMandatoryRoom(room: string): boolean {
    if (room === ALL_CHAT_ROOMS) return true;
    const desc = this.descriptors.get(room);
    return !!desc && classRank(desc.classification) > classRank("UNCLASSIFIED");
  }

  /** Can this client LEAVE/hide `room`? false for the broadcast room + mandatory
   *  classification-floor rooms; true for optional rooms. */
  canLeaveRoom(room: string): boolean {
    return !this.isMandatoryRoom(room);
  }

  /** Is `room` currently soft-left (hidden from this client's switcher)? */
  isRoomHidden(room: string): boolean {
    return this.hiddenRooms.has(room);
  }

  /** SOFT-LEAVE a room (C3-CHAT): hide it from THIS client's switcher — a LOCAL view filter
   *  only (no wire effect; Glomar-safe — it can only ever REMOVE an already-cleared row).
   *  REFUSES a mandatory room (throws CANNOT_LEAVE). Rejoin via openRoom (un-hides). If the
   *  left room was current, fall back to the always-visible broadcast room. */
  leaveRoom(room: string): void {
    if (this.isMandatoryRoom(room)) {
      throw new Error(
        `CANNOT_LEAVE: room "${room}" is mandatory (broadcast or classification-floor) — not removable`,
      );
    }
    this.hiddenRooms.add(room);
    if (this.currentRoom === room) {
      this.currentRoom = ALL_CHAT_ROOMS;
      this.markPresent(this.currentRoom, this.subject.id);
      this.setReadHighWater(this.currentRoom);
    }
    this.notifyChange();
  }

  /** IDENTITY-SWITCH REBUILD (R2, §4.3). CLEAR the per-room decoded-body store + all
   *  room-derived state, set the new subject, then re-ingest ONLY the supplied
   *  currently-visible (post-PDP-seam) envelopes for the new subject. A HIGH body
   *  decoded under HIGH VANISHES on a switch-to-LOW: the store is rebuilt from scratch
   *  (default to HIDING), and a now-invisible room is simply absent from `visible`, so
   *  it produces no row. The host supplies the projection's currently-visible set; an
   *  empty/omitted set rebuilds to EMPTY (fail-closed). The §2.2 plaintext cache is
   *  cleared in lockstep by the host's chatKeyAccess (on setKasToken). */
  async setSubject(
    subject: { id: string } & Record<string, unknown>,
    visible: CanonicalEnvelope[] = [],
  ): Promise<void> {
    this.subject = subject;
    // Default to HIDING: wipe EVERYTHING the prior persona accumulated (R2-low — including
    // seqCounters + pendingPresence; no per-room state survives cross-persona).
    this.roomStore = new Map();
    this.presence = new Map();
    this.roster = new Map();
    this.descriptors = new Map();
    this.rawEnvelopes = new Map();
    this.seqCounters = new Map();
    this.pendingPresence = new Set();
    // C3-CHAT — read-state + soft-leave are PER-PERSONA: wipe them too (default-to-fresh; the
    // new persona sees its full cleared list with everything unread until it reads/opens).
    this.hiddenRooms = new Set();
    this.roomRead = new Map();
    // R1 — RESET currentRoom to the always-visible broadcast room BEFORE the rebuild loop,
    // so at NO observable point during the switch does currentRoom name a hidden room. (The
    // prior persona's open room may be invisible to the new/lower subject.)
    this.currentRoom = ALL_CHAT_ROOMS;
    // R1 — SUPPRESS per-envelope notifyChange during the rebuild: each onEnvelope fires
    // notifyChange, and React could flush across the async per-envelope rewrap awaits while
    // the store is half-built. The `rebuilding` flag gates notifyChange; one fires at the end.
    this.rebuilding = true;
    try {
      // Rebuild from scratch over ONLY the currently-visible envelopes for the new subject.
      for (const env of visible) {
        await this.onEnvelope(env);
      }
    } finally {
      this.rebuilding = false;
    }
    // currentRoom is already the broadcast room (always visible). If the prior open room is
    // visible to the new subject, we still keep the broadcast default (default-to-HIDING —
    // never auto-reopen a room across a persona switch). Mark present in the (visible) current room.
    if (this.canSee(this.currentRoom)) {
      this.markPresent(this.currentRoom, subject.id);
    }
    // The landing (broadcast) room is "open" for the new persona → mark it read so it does not
    // start with a stale badge over traffic the persona is arriving into (C3-CHAT, §7.7).
    this.setReadHighWater(this.currentRoom);
    // Fire EXACTLY ONCE now that the store is fully rebuilt + currentRoom is safe (R1).
    this.notifyChange();
  }

  // --- internals -------------------------------------------------------------

  /** Fire the change listeners (after every store mutation). A throwing listener is
   *  isolated so it can't break ingest. SUPPRESSED during a rebuild (R1) so no mid-loop
   *  render reads a stale/hidden currentRoom; setSubject fires exactly once at the end. */
  private notifyChange(): void {
    if (this.rebuilding) return;
    for (const cb of this.changeListeners) {
      try {
        cb();
      } catch {
        // a bad listener must not break ingest
      }
    }
  }

  private sendCommand(args: unknown): Promise<void> {
    if (args && typeof args === "object" && "body" in args) {
      const a = args as { body: string; room?: string; classification?: string };
      return this.send(a.body, a.room ?? this.currentRoom, a.classification);
    }
    if (typeof args === "string") return this.send(args);
    return Promise.resolve();
  }

  /** Bus delivery: ignore non-chat envelopes; route descriptor vs presence vs message.
   *  Room isolation: an envelope is filed strictly under ITS room — never another. */
  private async onEnvelope(env: CanonicalEnvelope): Promise<void> {
    if (isRoomDescriptor(env)) {
      this.ingestDescriptor(env);
      return;
    }
    if (!isChatMessage(env)) {
      // Presence control envelopes are also `kind: "message"` but carry the
      // presence author + a presence action in properties.
      if (env.kind === "message" && env.properties.author === PRESENCE_AUTHOR) {
        this.onPresence(env);
      }
      return;
    }
    await this.ingest(env);
  }

  /** File a room DESCRIPTOR (§4.1). A descriptor makes a zero-message room listable +
   *  the marking authoritative. FAIL-CLOSED: empty/whitespace room name → drop + log
   *  (never coerce to a default room — §4.5). It already passed the PDP seam (visible). */
  private ingestDescriptor(env: CanonicalEnvelope): void {
    const room = envelopeRoom(env);
    if (!room || !room.trim()) {
      // eslint-disable-next-line no-console
      console.error("[cap-chat] dropping room descriptor with empty/absent room name (fail-closed)", env.id);
      return;
    }
    this.rawEnvelopes.set(env.id, env);
    const displayName =
      typeof env.properties.displayName === "string" ? env.properties.displayName : room;
    const incomingClass = env.security.classification;
    const incomingRel = env.security.releasability ?? [];
    // MERGE two descriptor floors for the SAME room (MOST-RESTRICTIVE on BOTH axes — the same
    // invariant as the marking chokepoint, applied to floor-vs-floor merge):
    //   classification = MAX(prev, incoming)   — a lower-class descriptor never MASKS a higher one.
    //   releasability  = INTERSECTION(prev, incoming) — ALWAYS, regardless of which class wins. A
    //     higher-class descriptor NEVER licenses a WIDER audience (the leak this closes). An empty
    //     intersection (contradiction) keeps the prior set + logs (fail-closed; never widen/union).
    const prev = this.descriptors.get(room);
    let floorClass: string;
    let floorRel: string[];
    if (!prev) {
      floorClass = incomingClass;
      floorRel = incomingRel;
    } else {
      floorClass =
        classRank(prev.classification) >= classRank(incomingClass) ? prev.classification : incomingClass;
      const intersection = prev.releasability.filter((r) => incomingRel.includes(r));
      if (intersection.length === 0 && (prev.releasability.length > 0 || incomingRel.length > 0)) {
        // eslint-disable-next-line no-console
        console.error(
          `[cap-chat] contradictory releasability for room "${room}" (prev=${JSON.stringify(prev.releasability)} incoming=${JSON.stringify(incomingRel)}) — keeping prior (never widen)`,
        );
        floorRel = prev.releasability;
      } else {
        floorRel = intersection;
      }
    }
    this.descriptors.set(room, { displayName, classification: floorClass, releasability: floorRel });
    // Ensure the room is listable even with zero messages.
    if (!this.roomStore.has(room)) this.roomStore.set(room, new Map());
    // G1 — RE-PUBLISH ON DESCRIPTOR ARRIVAL: if this is the room the subject JOINED whose
    // presence was SUPPRESSED (floor was unknown), announce presence NOW at the known floor.
    // (Skipped during a rebuild — setSubject re-announces presence for the current room once.)
    if (!this.rebuilding && this.pendingPresence.has(room)) {
      void this.publishPresence(room, true, floorClass, floorRel);
    }
    this.notifyChange();
  }

  /** File a chat envelope into its room store, decoding the body for the acting
   *  subject (E2E rewrap — R1 async). De-dup by envelope id. FAIL-CLOSED: empty/absent
   *  room → drop + log (never coerce to a default room — §4.5). */
  private async ingest(env: CanonicalEnvelope): Promise<void> {
    const room = envelopeRoom(env);
    if (!room || !room.trim()) {
      // eslint-disable-next-line no-console
      console.error("[cap-chat] dropping chat message with empty/absent room (fail-closed)", env.id);
      return;
    }
    this.rawEnvelopes.set(env.id, env);
    const msg = await decodeMessage(env, this.keyAccess, this.subject);
    const store = this.roomStore.get(room) ?? new Map<string, ChatMessage>();
    store.set(msg.id, msg);
    this.roomStore.set(room, store);
    // The author is present in the room they spoke in.
    this.markPresent(room, msg.author);
    // READ-STATE (C3-CHAT): a message arriving in the OPEN room stays read (you're looking at
    // it); traffic in any OTHER room accrues unread. SUPPRESSED during a rebuild (R1) so the
    // identity-switch re-ingest does not silently zero the new persona's fresh unread.
    if (!this.rebuilding && room === this.currentRoom && !this.hiddenRooms.has(room)) {
      this.setReadHighWater(room);
    }
    this.notifyChange();
  }

  private onPresence(env: CanonicalEnvelope): void {
    const room = envelopeRoom(env);
    if (!room) return;
    this.rawEnvelopes.set(env.id, env);
    const who = typeof env.properties.source === "string" ? env.properties.source : undefined;
    if (!who) return;
    const rosterKey = `${room} ${who}`;
    if (env.properties.presence === "leave") {
      this.presence.get(room)?.delete(who);
      // A leave retires the roster contact too — a gone client is neither capable nor
      // legacy-present (the bridge gate folds off the SAME roster).
      this.roster.delete(rosterKey);
    } else {
      this.markPresent(room, who);
      // ROSTER (C5): record the live beacon + its parsed `<__tdfcap>` flag for the pure
      // capability selectors. The newest beacon REPLACES the prior one for this contact.
      const contact = contactFromPresence(env);
      if (contact) this.roster.set(rosterKey, contact);
    }
    this.notifyChange();
  }

  private markPresent(room: string, who: string): void {
    const set = this.presence.get(room) ?? new Set<string>();
    set.add(who);
    this.presence.set(room, set);
  }

  /** Can the CURRENT subject see this room? The broadcast room is always visible; any
   *  other room is visible iff the subject received a cleared (post-PDP-seam) envelope for
   *  it — i.e. it is in the post-seam store or has a descriptor. A hidden room is absent
   *  from both (Glomar), so this returns false. Drives the P2 currentRoom reset. */
  private canSee(room: string): boolean {
    return room === ALL_CHAT_ROOMS || this.roomStore.has(room) || this.descriptors.has(room);
  }

  /** DISPLAY-ONLY (row-badge fold): the MAX classification across this room's ingested
   *  (post-seam) MESSAGE envelopes — used for the rooms() row badge when NO descriptor is
   *  known, so a message-only SECRET room shows SECRET (not the bare UNCLASSIFIED fallback).
   *  Folds STRICTLY off `rawEnvelopes` (already past the PDP seam — never a side-channel for
   *  a hidden room). Returns UNCLASSIFIED for a zero-message room. NOT a gating input —
   *  `assertRoomMarking`/`roomClass` are untouched (this can never change the real room
   *  marking). Under ceiling semantics every message envelope is sealed at exactly the room
   *  class, so the max IS the room class for a message-only room. */
  private maxMessageClassification(room: string): string {
    let best = "UNCLASSIFIED";
    let bestRank = classRank(best);
    for (const env of this.rawEnvelopes.values()) {
      // Only this room's CHAT MESSAGES count (a descriptor would already win above; presence
      // control envelopes carry the room marking but are not "messages" for the badge). Room
      // isolation: an envelope filed under another room never bleeds into this badge.
      if (!isChatMessage(env) || envelopeRoom(env) !== room) continue;
      const cls = env.security.classification;
      const r = classRank(cls);
      // Take the strictly-higher KNOWN class. An unknown class ranks +Infinity (fail-closed)
      // but is NOT adopted as a display label (it would render a non-vocabulary string); the
      // ladder-known max is the right badge. The real gate still fails-closed in assertRoomMarking.
      if (Number.isFinite(r) && r > bestRank) {
        best = cls;
        bestRank = r;
      }
    }
    return best;
  }

  /** ───────────────────────────── THE ROOM-MARKING CHOKEPOINT ─────────────────────────────
   *  THE INVARIANT (single source of truth for "a room marking"):
   *    No envelope of ANY kind (message | presence | descriptor) may carry a marking —
   *    classification OR releasability — that permits a subject the room marking would deny;
   *    and no operation may WIDEN an established room marking's audience.
   *
   *  EVERY room-marking producer (send / sendClear / publishPresence / publishRoomDescriptor /
   *  the ingestDescriptor merge) routes its (classification, releasability) through here. The
   *  result is the MOST-RESTRICTIVE marking vs the KNOWN room marking (the ingested descriptor):
   *    - classification = MAX(operator, room class) — higher is more restrictive; a CONTROL
   *      envelope clamps UP to the room class (the Glomar guard: its shell names the room).
   *      For a MESSAGE, `assertRoomMarking` has already enforced the CEILING (pick <= room
   *      class) upstream, so this MAX resolves to EXACTLY the room class — the envelope seal
   *      is pinned at the room marking and the below-room pick survives only as the CONTENT
   *      marking inside the sealed body. Mechanically unchanged by the 2026-07-06 flip.
   *    - releasability   = INTERSECTION(operator, room) — NEVER wider than the room audience,
   *      regardless of any class change. An empty intersection (no overlap) clamps to the ROOM
   *      audience (never widens, never publishes to an audience the room would deny).
   *
   *  The room marking is `descriptors.get(room)` when KNOWN. When NO descriptor is known:
   *    - The broadcast room is the public marking (UNCLASSIFIED, broadly releasable).
   *    - A genuine CREATE (no prior marking) sets the room marking FREELY from the operator's
   *      explicit choice — that IS the first marking.
   *    - A NAMED room with NO known marking AND a RESTRICTED operator marking (class >
   *      UNCLASSIFIED OR any releasability restriction) FAILS CLOSED (`{ ok:false }`) — we
   *      cannot prove the marking is within the room's (unknown) audience, so we never emit it.
   *
   *  `establishFloor` distinguishes the two no-descriptor cases (the parameter name keeps the
   *  historical "floor" spelling — for CONTROL envelopes the room marking IS a floor they
   *  clamp UP to):
   *    - true  (a genuine CREATE: the first publishRoomDescriptor / the creator's open-time
   *             presence) → the EXPLICIT operator marking SETS the room marking freely (even
   *             restricted).
   *    - false (a RIDER: a join-presence with no explicit marking, or a message into a room
   *             whose descriptor we have not seen) → a RESTRICTED marking with no known room
   *             marking FAILS CLOSED (we cannot prove it is within the room's unknown audience).
   *
   *  Returns a discriminated result so each producer chooses its fail-closed behavior: a
   *  message THROWS, a presence/descriptor SUPPRESSES. */
  private clampMarkingToFloor(
    room: string,
    classification: string,
    releasability: string[] | undefined,
    establishFloor: boolean,
    isBeacon = false,
  ): { ok: true; classification: string; releasability: string[] } | { ok: false; reason: string } {
    const opRel = releasability ?? [];
    if (room === ALL_CHAT_ROOMS) {
      // The broadcast room's marking is UNCLASSIFIED. Under CEILING semantics (2026-07-06) a
      // MESSAGE can never arrive here with a pick above UNCLASSIFIED — `assertRoomMarking`
      // throws ROOM_CEILING upstream (suggesting a classified room instead), so for messages
      // pick <= UNCLASSIFIED ⇒ pick == UNCLASSIFIED and the MAX below is a no-op. The MAX is
      // KEPT (mechanically unchanged) as the CONTROL-envelope clamp-UP + defense-in-depth: a
      // hypothetical higher marking reaching this branch is RAISED, never lowered (the old
      // under-classification leak — a SECRET-intended body sealed UNCLASSIFIED — stays
      // impossible in both directions). The releasability stays the operator's: an EMPTY `[]`
      // IS the release-to-everyone marking (no narrowing). NATO is not a legal defense.local
      // releasability and threw on seal; `[]` validates + seals.
      const floorClass =
        classRank(classification) >= classRank("UNCLASSIFIED") ? classification : "UNCLASSIFIED";
      return { ok: true, classification: floorClass, releasability: opRel };
    }

    const desc = this.descriptors.get(room);
    if (desc) {
      // KNOWN room marking (an ingested descriptor) — clamp to it on BOTH axes. A CONTROL
      // envelope clamps UP to the room class (Glomar); a MESSAGE arrives ceiling-checked
      // (pick <= room class), so the MAX pins its envelope at EXACTLY the room class. The
      // releasability is ALWAYS intersected with the room audience (never widened).
      const floorClass =
        classRank(classification) >= classRank(desc.classification) ? classification : desc.classification;
      const intersection = opRel.filter((r) => desc.releasability.includes(r));
      const floorRel = intersection.length > 0 ? intersection : desc.releasability;
      return { ok: true, classification: floorClass, releasability: floorRel };
    }

    // NO descriptor known, and this caller is a RIDER (not establishing the floor) → it cannot
    // prove the room's audience. Fail-closed policy by producer kind:
    //   - A BEACON (presence/descriptor — pure room-existence) RIDER ALWAYS fails closed for a
    //     named room: a bare join / a rel-restricted re-publish into an unknown floor is the
    //     Glomar leak (G1 + the LOW UNCLAS//REL-FVEY case). Only a genuine CREATE (establishFloor)
    //     or the broadcast room emits a beacon without a known floor.
    //   - A MESSAGE rider reveals its own room by existing, so it fails closed only when the
    //     marking is CLASS-restricted (> UNCLASSIFIED) — an UNCLAS//REL send into a yet-undescribed
    //     room is the legitimate first marking, not a widening of any (nonexistent) floor.
    if (!establishFloor) {
      const classRestricted = classRank(classification) > classRank("UNCLASSIFIED");
      if (isBeacon || classRestricted) {
        return { ok: false, reason: `floor for room "${room}" is unknown` };
      }
    }
    // A genuine CREATE (establishFloor) sets the floor freely from the explicit operator marking;
    // a non-beacon UNCLASSIFIED/public marking carries no audience secret → the marking stands.
    // An OMITTED releasability → the PUBLIC, UNRESTRICTED audience `[]` (release-to-everyone). A
    // coalition tag like NATO is NOT a legal defense.local releasability and threw on seal; `[]`
    // validates + seals as the broadest audience.
    return { ok: true, classification, releasability: opRel };
  }

  /** The authoritative ROOM CLASSIFICATION: the descriptor's marking if known, else the
   *  supplied default. The descriptor (creator-set) is authoritative (L2). Post the
   *  2026-07-06 ceiling flip this is BOTH the SEAL class for every room envelope AND the
   *  CEILING for a message's content pick (it was previously read as a message floor). */
  private roomClass(room: string, fallback: string): string {
    const desc = this.descriptors.get(room);
    if (desc) return desc.classification;
    if (room === ALL_CHAT_ROOMS) return "UNCLASSIFIED";
    return fallback;
  }

  /** The authoritative floor MARKING for a room CONTROL envelope (presence), via the SINGLE
   *  chokepoint. `fallbackClass` present ⇒ an EXPLICIT open-time floor was chosen (a create
   *  establisher); absent ⇒ a bare join (a rider that fails closed for a restricted unknown
   *  floor). Returns `undefined` when the chokepoint fails closed — the caller then SUPPRESSES
   *  the presence (never emits a clear control envelope carrying a restricted room's name). */
  private roomFloorMarking(
    room: string,
    fallbackClass?: string,
    fallbackRel?: string[],
  ): { classification: string; releasability: string[] } | undefined {
    const establishFloor = fallbackClass !== undefined;
    // isBeacon=true: a presence BEACON is pure room-existence, so a RIDER (bare join, or a
    // rel-restricted re-publish) into an unknown-floor room must fail closed (G1 + point 4) —
    // never leak the room via a clear presence. Only a create or the broadcast room emits one.
    const clamped = this.clampMarkingToFloor(
      room,
      fallbackClass ?? "UNCLASSIFIED",
      fallbackRel,
      establishFloor,
      /* isBeacon */ true,
    );
    return clamped.ok ? { classification: clamped.classification, releasability: clamped.releasability } : undefined;
  }

  private nextSeq(room: string): number {
    const n = (this.seqCounters.get(room) ?? 0) + 1;
    this.seqCounters.set(room, n);
    return n;
  }

  /** Publish a room DESCRIPTOR (§4.1) once per room this client opens. Idempotent at
   *  the wire level (the descriptor id is `chat-room:<room>`, de-duped on ingest). */
  private async publishRoomDescriptor(
    room: string,
    classification: string,
    releasability: string[],
  ): Promise<void> {
    if (!this.ctx) return;
    // The fixed broadcast room is implicit — never published as a descriptor (it is
    // always present, never Glomar-hidden).
    if (room === ALL_CHAT_ROOMS) return;
    // CHOKEPOINT: when a descriptor floor is ALREADY KNOWN, clamp the new descriptor to it (a
    // re-open may RAISE class but can NEVER widen releasability beyond the known floor). ONLY
    // the FIRST descriptor (no prior floor → establishFloor) sets the floor freely (a genuine
    // create). A fail-closed result means a restricted re-publish with an unknown floor — skip
    // it (never publish a descriptor that would WIDEN/leak the room).
    const establishFloor = !this.descriptors.has(room);
    // isBeacon=true: a DESCRIPTOR is the room-existence beacon; a re-publish into an unknown floor
    // (a rider) must fail closed (point 4). The FIRST descriptor (establishFloor) sets freely.
    const clamped = this.clampMarkingToFloor(room, classification, releasability, establishFloor, /* isBeacon */ true);
    if (!clamped.ok) {
      // eslint-disable-next-line no-console
      console.error(
        `[cap-chat] suppressing descriptor publish for room "${room}" — ${clamped.reason} (fail-closed; would widen/leak the room)`,
      );
      return;
    }
    const env = buildRoomDescriptorEnvelope(
      { room, displayName: room, creator: this.subject.id, time: new Date().toISOString() },
      clamped.classification,
      clamped.releasability,
    );
    await this.ctx.data.publish(env);
  }

  /** Publish a presence join/leave as a `kind: "message"` control envelope.
   *
   *  ROOM-FLOOR SEAL (P1 — Glomar leak fix). A presence envelope carries the room's
   *  name (`mission[0]`/`properties.room`), so it MUST be marked at the ROOM FLOOR — the
   *  SAME authority `send()`/the descriptor use — or the PDP permits it to an uncleared
   *  viewer and the COP host folds the room name into the banner/census (the room's
   *  EXISTENCE + who is in it leaks). FAIL CLOSED: for a named (non-broadcast) room with
   *  no known floor, DO NOT publish a presence carrying its name — skip it (no clear leak).
   *  The broadcast room stays UNCLASSIFIED (its public floor). */
  private async publishPresence(
    room: string,
    join: boolean,
    fallbackClass?: string,
    fallbackRel?: string[],
  ): Promise<void> {
    if (!this.ctx) return;
    const floor = this.roomFloorMarking(room, fallbackClass, fallbackRel);
    if (!floor) {
      // FAIL CLOSED — no known floor for a restricted/unknown room: never emit a clear
      // presence that would leak the room's existence past the PDP seam. Remember the room
      // so presence is RE-PUBLISHED at the correct floor once a descriptor arrives (G1).
      if (join) this.pendingPresence.add(room);
      // eslint-disable-next-line no-console
      console.error(
        `[cap-chat] suppressing presence for room "${room}" — no known floor (fail-closed; would leak room existence)`,
      );
      return;
    }
    // The floor is known → this join is announced; no longer pending.
    if (join) this.pendingPresence.delete(room);
    const time = new Date().toISOString();
    // A presence beacon stays FRESH for a finite window (the SA stale window) so a peer's
    // capability selector reads it as live. A `leave` is point-in-time (stale == now).
    const stale = join
      ? new Date(Date.now() + PRESENCE_STALE_MS).toISOString()
      : time;
    const env: CanonicalEnvelope = {
      id: `${this.id}:presence:${room}:${this.subject.id}:${Date.now()}`,
      schema: "scop.envelope/1.0",
      kind: "message",
      time,
      valid_time: { start: time, stale },
      geometry: null,
      properties: {
        name: `presence:${room}`,
        type: "chat-presence",
        author: PRESENCE_AUTHOR,
        source: this.subject.id,
        room,
        presence: join ? "join" : "leave",
        // CAPABILITY ADVERTISEMENT (C5, §5.1): a TDF-capable client rides `<__tdfcap>` on
        // EVERY presence beacon so a peer detects it as `capable`. A non-capable (legacy)
        // emitter omits it => the peer's selector defaults it to `legacy` (unknown-as-legacy).
        ...(this.tdfCapable && join ? { tdfcap: { v: "1", alg: "ZTDF" } } : {}),
        // Display callsign (client-asserted) — powers callsign rendering + DM autocomplete.
        ...(join && this.currentCallsign() ? { callsign: this.currentCallsign() } : {}),
      },
      // Sealed at the ROOM FLOOR (NOT the UNCLASSIFIED default) — same authority as a
      // message/descriptor, so the PDP denies it to an uncleared viewer (Glomar).
      security: roomLabels(room, floor.classification, floor.releasability),
      provenance: { originator: this.subject.id, adapter: "cap-chat", received_at: time, chain: [] },
      // Presence carries no protected body.
      payload: { clear: { presence: join ? "join" : "leave" } },
    };
    await this.ctx.data.publish(env);
  }
}

/** Factory mirroring the "a capability is a module exporting one Capability" shape
 *  in the contract — convenient for the host's lazy loader. */
export function createChatCapability(opts: ChatCapabilityOptions): ChatCapability {
  return new ChatCapability(opts);
}
